import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { NavEntryPoint, NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { loyaltyRouter } from './routing/loyaltyRouter'
import { loyaltyPartnerScopedRouter } from './routing/loyaltyPartnerScopedRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { loyaltyAccessControl } from './access-control/loyaltyAccess.configuration'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'loyalty',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'loyalty-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  routers: {
    scoped: [loyaltyRouter],
    partnerScoped: [loyaltyPartnerScopedRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.Merchandising,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create and manage customer loyalty reward programs with customizable point systems, theming, and sign-up options',
          labelId: 'loyaltyDomain.navigation.loyaltyPrograms',
          route: 'loyalty-list',
          accessControl: loyaltyAccessControl,
          positionNavV2: 10, // Position in the merchandising section
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Storefront,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create and manage customer loyalty reward programs with customizable point systems, theming, and sign-up options',
          labelId: 'loyaltyDomain.navigation.loyaltyPrograms',
          labelIdV3: 'loyaltyDomain.navigation.loyaltyPrograms.v3',
          route: 'loyalty-list-storefront',
          accessControl: loyaltyAccessControl,
          positionNavV3: 4,
        },
      ],
    },
    {
      // Per-L1 flavored routes — each clones `loyalty-list` and renders the
      // same partner-scoped page with a per-L1 `extraStoreConfigFilter`
      // (OnlyMarketplace / OnlyStorefront / OnlyStorefrontPro). The v2
      // Merchandising block above still resolves to the un-flavored
      // `loyalty-list` route. SFP is explicitly attached because L1
      // attachment is not inherited from Storefront.
      attachToV3: NavEntryPointV3.Marketplace,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create and manage customer loyalty reward programs with customizable point systems, theming, and sign-up options',
          labelId: 'loyaltyDomain.navigation.loyaltyPrograms',
          labelIdV3: 'loyaltyDomain.navigation.loyaltyPrograms.v3',
          route: 'loyalty-list-marketplace',
          accessControl: loyaltyAccessControl,
          positionNavV3: 2,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.StorefrontPro,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create and manage customer loyalty reward programs with customizable point systems, theming, and sign-up options',
          labelId: 'loyaltyDomain.navigation.loyaltyPrograms',
          labelIdV3: 'loyaltyDomain.navigation.loyaltyPrograms.v3',
          route: 'loyalty-list-storefront-pro',
          accessControl: loyaltyAccessControl,
          positionNavV3: 8,
        },
      ],
    },
  ],
  adminNavigationEntries: [],
  // NOTE TO DEV: run `yarn ipp domain register <your-system-name>` to register your domain with Totem here
}

export default definition
